import { Box, Button, TextField, Typography } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { useIntl } from "react-intl";
import Messages from "../../localization/Messages";
import { DateFormat, getDateMoment, getDateNumber } from "../../helpers/dateHelper";
import { getLDExpensePerShipStats, getLDShipAndDockExpenseStats } from "../../api/stats";
import {
    getFileName,
    getLDExpensePerShipStatisticsValues,
    getLDShipAndDockStatisticsValues,
} from "./shared/exportUtils";
import { CsvStatisticsFilters } from "../StatisticsPage/shared/filters";

import { showBackendMessage } from "../../helpers/messagesHelper";
import {
    statsLDExpensePerShipColumnDefs,
    statsPerShipAndDockColumnDefs,
} from "../StatisticsPage/shared/constants";
import { useDispatch } from "react-redux";
import { useGuiConfState } from "../../hooks/useGuiConfState";
import { useCustomExportToCsv } from "../../hooks/useCustomExportToCsv";
import { useCustomExportToXls } from "../../hooks/useCustomExportToXls";
import { useState } from "react";
import { ILdExpensePerShipStat } from "../../models/stats";

export const LDExportStatisticsContainer = () => {
    const exportCsv = useCustomExportToCsv();
    const exportXlsx = useCustomExportToXls();
    const intl = useIntl();
    const dispatch = useDispatch();
    const [csvFilters, setCsvFilters] = useGuiConfState("ldCsvStatistics");
    const [loading, setLoading] = useState(false);

    const handleChange = (diff: Partial<CsvStatisticsFilters>) => {
        setCsvFilters(diff);
    };

    const onExportShipAndDockStatistics = async (csvFilters: CsvStatisticsFilters) => {
        try {
            setLoading(true);
            const stats = await getLDShipAndDockExpenseStats(
                csvFilters.from ?? 0,
                csvFilters.to ?? new Date().getTime(),
            );

            const values = stats.map((data: any) =>
                getLDShipAndDockStatisticsValues(
                    data,
                    csvFilters
                ),
            );

            const fileName = getFileName(
                intl.formatMessage(Messages.expensePerShipAndDockStatistics),
                csvFilters.from,
                csvFilters.to,
            );
            exportCsv(statsPerShipAndDockColumnDefs, values, undefined, fileName);
        } catch {
            dispatch(showBackendMessage(intl, "error", "fetching", Messages.shipAndDockStatistics));
        } finally {
            setLoading(false);
        }
    };

    const onExportExpensePerShipStatisticsCsv = async (csvFilters: CsvStatisticsFilters) => {
        try {
            setLoading(true);
            const stats = await getLDExpensePerShipStats(
                csvFilters.from ?? 0,
                csvFilters.to ?? new Date().getTime(),
            );

            const uniqueDocksMap = new Map<string, string>();
            stats.forEach((stat: ILdExpensePerShipStat) => {
                stat.arrivals?.forEach(arrival => {
                    const dockId = arrival.dockId ?? "null";
                    if (!uniqueDocksMap.has(dockId)) {
                        uniqueDocksMap.set(
                            dockId,
                            arrival.dockName ?? intl.formatMessage(Messages.unknownDock),
                        );
                    }
                });
            });
            const uniqueDocks = Array.from(uniqueDocksMap.entries())
                .map(([id, name]) => ({ id, name }))
                .sort((a, b) => {
                    if (a.id === "null") return 1;
                    if (b.id === "null") return -1;
                    return 0;
                });
            const additionalColumns = uniqueDocks.map(d => d.name);

            const values = stats.map((data: ILdExpensePerShipStat) =>
                getLDExpensePerShipStatisticsValues(data, csvFilters, uniqueDocks),
            );

            const fileName = getFileName(
                intl.formatMessage(Messages.exportExpensePerShipStatistics),
                csvFilters.from,
                csvFilters.to,
            );
            exportCsv(statsLDExpensePerShipColumnDefs, values, additionalColumns, fileName);
        } catch {
            dispatch(
                showBackendMessage(intl, "error", "fetching", Messages.expensePerShipStatistics),
            );
        } finally {
            setLoading(false);
        }
    };

    const onExportExpensePerShipStatisticsXlsx = async (csvFilters: CsvStatisticsFilters) => {
        try {
            setLoading(true);
            const stats = await getLDExpensePerShipStats(
                csvFilters.from ?? 0,
                csvFilters.to ?? new Date().getTime(),
            );

            const uniqueDocksMap = new Map<string, string>();
            stats.forEach((stat: ILdExpensePerShipStat) => {
                stat.arrivals?.forEach(arrival => {
                    const dockId = arrival.dockId ?? "null";
                    if (!uniqueDocksMap.has(dockId)) {
                        uniqueDocksMap.set(
                            dockId,
                            arrival.dockName ?? intl.formatMessage(Messages.unknownDock),
                        );
                    }
                });
            });
            const uniqueDocks = Array.from(uniqueDocksMap.entries())
                .map(([id, name]) => ({ id, name }))
                .sort((a, b) => {
                    if (a.id === "null") return 1;
                    if (b.id === "null") return -1;
                    return 0;
                });
            const additionalColumns = uniqueDocks.map(d => d.name);

            const values = stats.map((data: ILdExpensePerShipStat) =>
                getLDExpensePerShipStatisticsValues(data, csvFilters, uniqueDocks),
            );

            const fileName = getFileName(
                intl.formatMessage(Messages.exportExpensePerShipStatistics),
                csvFilters.from,
                csvFilters.to,
            );
            exportXlsx(statsLDExpensePerShipColumnDefs, values, additionalColumns, fileName);
        } catch {
            dispatch(
                showBackendMessage(intl, "error", "fetching", Messages.expensePerShipStatistics),
            );
        } finally {
            setLoading(false);
        }
    };

    const onExportShipAndDockStatisticsXlsx = async (csvFilters: CsvStatisticsFilters) => {
        try {
            setLoading(true);
            const stats = await getLDShipAndDockExpenseStats(
                csvFilters.from ?? 0,
                csvFilters.to ?? new Date().getTime(),
            );

            const values = stats.map((data: any) =>
                getLDShipAndDockStatisticsValues(
                    data,
                    csvFilters
                ),
            );

            const fileName = getFileName(
                intl.formatMessage(Messages.expensePerShipAndDockStatistics),
                csvFilters.from,
                csvFilters.to,
            );
            exportXlsx(statsPerShipAndDockColumnDefs, values, undefined, fileName);
        } catch {
            dispatch(showBackendMessage(intl, "error", "fetching", Messages.shipAndDockStatistics));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box className="export-statistics-container shadow" gap={3}>
            <Typography variant="caption" fontWeight={"bold"} fontSize={"20px"}>
                {intl.formatMessage(Messages.exportStatisticLists)}
            </Typography>
            <Box display="flex " gap={2} className="export-csvFilters">
                <DesktopDatePicker
                    label={intl.formatMessage(Messages.from)}
                    inputFormat={DateFormat.CLIENT_DATE}
                    value={getDateMoment(csvFilters.from)}
                    onChange={value =>
                        (!isNaN(getDateNumber(value)) || value === null) &&
                        handleChange({ from: getDateMoment(value).valueOf() })
                    }
                    renderInput={params => <TextField variant="filled" {...params} />}
                />
                <DesktopDatePicker
                    label={intl.formatMessage(Messages.to)}
                    inputFormat={DateFormat.CLIENT_DATE}
                    value={getDateMoment(csvFilters.to)}
                    onChange={value =>
                        (!isNaN(getDateNumber(value)) || value === null) &&
                        handleChange({ to: getDateMoment(value).valueOf() })
                    }
                    renderInput={params => <TextField variant="filled" {...params} />}
                />
            </Box>

            <Box className="export-buttons" display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                <Button
                    disabled={loading}
                    variant="contained"
                    color="primary"
                    onClick={() => onExportShipAndDockStatisticsXlsx(csvFilters)}
                >
                    {intl.formatMessage(Messages.expensePerShipAndDockStatistics)} (XLSX)
                </Button>
                <Button
                    disabled={loading}
                    variant="contained"
                    color="primary"
                    onClick={() => onExportExpensePerShipStatisticsXlsx(csvFilters)}
                >
                    {intl.formatMessage(Messages.exportExpensePerShipStatistics)} (XLSX)
                </Button>
                <Button
                    disabled={loading}
                    variant="contained"
                    color="primary"
                    onClick={() => onExportShipAndDockStatistics(csvFilters)}
                >
                    {intl.formatMessage(Messages.expensePerShipAndDockStatistics)} (CSV)
                </Button>
                <Button
                    disabled={loading}
                    variant="contained"
                    color="primary"
                    onClick={() => onExportExpensePerShipStatisticsCsv(csvFilters)}
                >
                    {intl.formatMessage(Messages.exportExpensePerShipStatistics)} (CSV)
                </Button>
            </Box>
        </Box>
    );
};
